import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedModule } from "../../../../shared/shared.module";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { NC_STATS_DATA, NcAxisBucket } from "../../models/data.model";
import { exportNcStatsExcel } from "./nc-stats-excel.utils";
import { NC_ORIGINE_COLORS } from "./widget-nc-stats.component";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import {
  ExcelExportConfirmDialogComponent,
  ExcelExportConfirmData,
  ExcelExportConfirmResult,
} from "../../../../shared/components/excel-export-confirm-dialog/excel-export-confirm-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

export interface NcStatsDialogData {
  data: NC_STATS_DATA;
  start?: string;
  end?: string;
}

interface OrigineRow {
  key: string;
  label: string;
  count: number;
  color: string;
  percentage: string;
}

/**
 * Palette Chart.js par défaut, utilisée pour la rotation par ligne dans
 * les sections sans sémantique forte (Par type, Top tiers, Top articles,
 * etc.). Permet de distinguer visuellement les entrées même quand elles
 * ont la même valeur.
 */
const NC_BAR_PALETTE = [
  "#36A2EB",
  "#FF6384",
  "#FF9F40",
  "#FFCD56",
  "#4BC0C0",
  "#9966FF",
  "#C9CBCF",
  "#8FCBFF",
  "#FFA1B5",
  "#A0D8B3",
];

@Component({
  selector: "vl-nc-stats-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    DatePipe,
    DecimalPipe,
    ExcelExportButtonComponent,
    RadioCardComponent,
  ],
  templateUrl: "./nc-stats-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NcStatsDialogComponent {
  data = inject<NcStatsDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<NcStatsDialogComponent>);
  private articlesInfos = inject(ArticlesInfosFacadeService);

  exporting = signal(false);

  byType = computed<NcAxisBucket[]>(() => this.data.data?.analytics?.byType ?? []);
  byGravite = computed<NcAxisBucket[]>(() => this.data.data?.analytics?.byGravite ?? []);
  byNature = computed<NcAxisBucket[]>(() => (this.data.data?.analytics?.byNature ?? []).slice(0, 10));
  topTiers = computed<NcAxisBucket[]>(() => (this.data.data?.analytics?.topTiers ?? []).slice(0, 10));
  /**
   * Top articles enrichis avec le nom (denomination) et le code via le
   * cache `ArticlesInfosFacadeService`. Le backend renvoie l'`articleId`
   * en `key` et `label`, on résout côté client (même pattern que la page
   * Analyse NC pour cohérence).
   */
  topArticles = computed<NcAxisBucket[]>(() => {
    const raw = (this.data.data?.analytics?.topArticles ?? []).slice(0, 10);
    return raw.map((b) => {
      const infos = this.articlesInfos.getByArticleId(b.key)?.();
      return {
        ...b,
        label: infos?.denomination ?? b.label,
      };
    });
  });
  byMonth = computed(() => this.data.data?.analytics?.byMonth ?? []);
  criticalOpen = computed(() => this.data.data?.analytics?.criticalOpen ?? []);

  /** Couleur par index de ligne pour distinguer visuellement les entrées. */
  colorAt(idx: number): string {
    return NC_BAR_PALETTE[idx % NC_BAR_PALETTE.length];
  }

  origineRows = computed<OrigineRow[]>(() => {
    const r = this.data.data?.stats?.origineRepartition;
    if (!r) return [];
    const total = r.interne + r.externe + r.mixte + r.nonRenseigne;
    const pct = (n: number) =>
      total > 0 ? ((n / total) * 100).toFixed(0) : "0";
    return [
      { key: "interne", label: "Interne", count: r.interne, color: NC_ORIGINE_COLORS.interne, percentage: pct(r.interne) },
      { key: "externe", label: "Externe", count: r.externe, color: NC_ORIGINE_COLORS.externe, percentage: pct(r.externe) },
      { key: "mixte", label: "Mixte", count: r.mixte, color: NC_ORIGINE_COLORS.mixte, percentage: pct(r.mixte) },
      {
        key: "nonRenseigne",
        label: "Non renseigné",
        count: r.nonRenseigne,
        color: NC_ORIGINE_COLORS.nonRenseigne,
        percentage: pct(r.nonRenseigne),
      },
    ].filter((o) => o.count > 0);
  });

  /** Mapping gravité → palette Chart.js (cohérence avec les autres widgets). */
  graviteColor(key: string): string {
    if (key === "CRITIQUE") return "#FF6384";
    if (key === "MAJEURE") return "#FF9F40";
    return "#36A2EB";
  }

  /** Largeur de la barre proportionnelle au max du bucket. */
  barWidth(value: number, list: NcAxisBucket[]): string {
    const max = list.reduce((m, b) => Math.max(m, b.count), 0);
    return max > 0 ? `${(value / max) * 100}%` : "0%";
  }

  /** Idem pour le timeline byMonth. */
  monthBarWidth(count: number): string {
    const list = this.byMonth();
    const max = list.reduce((m, b) => Math.max(m, b.count), 0);
    return max > 0 ? `${(count / max) * 100}%` : "0%";
  }

  private dialogs = inject(DialogsService);

  async exportExcel() {
    const ref = this.dialogs.open(
      ExcelExportConfirmDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Exporter — Stats NC",
        subtitle:
          this.data.start && this.data.end
            ? `Plage active : ${this.fmtPeriod(this.data.start)} → ${this.fmtPeriod(this.data.end)}`
            : undefined,
        // Pas de période override pour l'instant — la donnée est pré-chargée
        // par le widget parent. On confirme juste l'export.
        allowCustomRange: false,
      } as ExcelExportConfirmData,
    );
    const confirm = (await firstValueFrom(ref.afterClosed())) as
      | ExcelExportConfirmResult
      | null;
    if (!confirm) return;

    this.exporting.set(true);
    try {
      await exportNcStatsExcel(this.data.data);
    } finally {
      this.exporting.set(false);
    }
  }

  private fmtPeriod(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return iso;
    }
  }
}
